import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import { publicApi } from '../utils/api';
import { Loader2 } from 'lucide-react';

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.href = href;
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

export default function Insights() {
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category') || '';
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const title = category ? `${category} Insights | Tactmark` : 'Football Match Analysis Insights | Tactmark';
    const description = category
      ? `${category} articles for football coaches, analysts, clubs, and academies.`
      : 'Practical guides on post-match intelligence, match evidence, coaching observations, recurring patterns, and training priorities.';

    document.title = title;
    setMeta('description', description);
    setMeta('robots', 'index,follow,max-image-preview:large');
    setCanonical(category
      ? `https://tactmark.com/insights?category=${encodeURIComponent(category)}`
      : 'https://tactmark.com/insights');
  }, [category]);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError('');

    publicApi.getInsights(category || undefined)
      .then(({ articles: loadedArticles }) => {
        if (!cancelled) setArticles(Array.isArray(loadedArticles) ? loadedArticles : []);
      })
      .catch((requestError) => {
        if (!cancelled) {
          setArticles([]);
          setError(requestError?.message || 'Insights could not be loaded.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [category]);

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <header className="max-w-3xl mx-auto px-6 pt-16 pb-4 text-center">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">Insights</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy-900 leading-tight mb-4">
          Post-match intelligence for coaching staff.
        </h1>
        <p className="text-gray-600 text-lg leading-relaxed">
          Tactmark turns match evidence, coaching observations, and historical patterns into structured post-match intelligence and training priorities.
        </p>
      </header>

      <nav className="max-w-3xl mx-auto px-6 pb-6 flex items-center justify-center gap-2 text-sm" aria-label="Insight categories">
        <Link to="/insights" className={`px-3 py-1.5 rounded-full transition-colors ${!category ? 'bg-navy-900 text-white' : 'text-navy-700 hover:bg-navy-50'}`}>All</Link>
        <Link to="/insights?category=Guide" className={`px-3 py-1.5 rounded-full transition-colors ${category === 'Guide' ? 'bg-navy-900 text-white' : 'text-navy-700 hover:bg-navy-50'}`}>Guides</Link>
        <Link to="/insights?category=Comparison" className={`px-3 py-1.5 rounded-full transition-colors ${category === 'Comparison' ? 'bg-navy-900 text-white' : 'text-navy-700 hover:bg-navy-50'}`}>Comparisons</Link>
        <Link to="/insights?category=Daily+Tactical+Note" className={`px-3 py-1.5 rounded-full transition-colors ${category === 'Daily Tactical Note' ? 'bg-navy-900 text-white' : 'text-navy-700 hover:bg-navy-50'}`}>Tactical Notes</Link>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {loading ? (
          <div className="text-center py-16" aria-live="polite">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
            <p className="text-gray-500 text-sm mt-4">Loading insights…</p>
          </div>
        ) : error ? (
          <div className="border border-gray-200 rounded-2xl p-10 text-center">
            <h2 className="text-navy-900 font-semibold text-xl mb-3">Insights are temporarily unavailable.</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              The content service did not return the article list. This page now shows an explicit error instead of an empty screen.
            </p>
            <Link to="/features" className="inline-block bg-navy-900 text-white font-medium px-6 py-2.5 rounded hover:bg-navy-800 transition-colors">
              Explore Tactmark features
            </Link>
          </div>
        ) : articles.length === 0 ? (
          <div className="border border-gray-200 rounded-2xl p-10 text-center">
            <h2 className="text-navy-900 font-semibold text-xl mb-3">No published insights in this category.</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Empty or malformed articles are withheld from the public index until they contain usable content.
            </p>
            <Link to="/insights" className="inline-block bg-navy-900 text-white font-medium px-6 py-2.5 rounded hover:bg-navy-800 transition-colors">
              View all insights
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 border-t border-b border-gray-200">
            {articles.map((article) => (
              <Link key={article.slug} to={`/insights/${article.slug}`} className="block py-6 group">
                <p className="text-gray-400 text-xs mb-1.5">
                  {article.category || 'Guide'}
                  {article.publish_date ? ` · ${formatDate(article.publish_date)}` : ''}
                </p>
                <h2 className="text-navy-900 font-semibold text-lg group-hover:text-navy-600 transition-colors">{article.title}</h2>
                {article.meta_description && (
                  <p className="text-gray-500 text-sm mt-1.5 leading-relaxed">{article.meta_description}</p>
                )}
              </Link>
            ))}
          </div>
        )}
      </main>

      <PublicFooter />
    </div>
  );
}
